import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ForceGraph2D, { ForceGraphMethods, NodeObject, LinkObject } from 'react-force-graph-2d';
import { fetchConnectedGraphData, addLink as addLinkService } from '../../lib/supabase/websites';
import { GraphData } from '../../types/website';
import { useTheme } from '../../context/ThemeContext';
import { Button, Icon } from '../../components/ui';

interface ForceSettings {
  linkDistance: number;
  attractionStrength: number;
  chargeStrength: number;
  nodeSize: number;
  zoomLevel: number;
  particleSpeed: number;
  particleCount: number;
}

interface WebsiteGraphProps {
  onWebsitesUpdated: () => void;
  forceRefresh?: number;
  isAddingLink: boolean;
  setIsAddingLink: (adding: boolean) => void;
  forceSettings: ForceSettings;
}

// Friendly labels for the colors offered in the Add Website form
const COLOR_LABELS: Record<string, string> = {
  '#d95636': 'Default',
  '#4361ee': 'Blue',
  '#10b981': 'Green',
  '#8b5cf6': 'Purple',
  '#14b8a6': 'Teal',
  '#ec4899': 'Pink',
  '#facc15': 'Yellow',
  '#f59e0b': 'Yellow',
  '#ef4444': 'Red',
};

const nodeKey = (n: string | number | NodeObject | undefined): string =>
  typeof n === 'object' && n !== null ? String(n.id) : String(n);

export default function WebsiteGraph({
  onWebsitesUpdated,
  forceRefresh,
  isAddingLink,
  setIsAddingLink,
  forceSettings,
}: WebsiteGraphProps) {
  const navigate = useNavigate();
  const [selectedFromNode, setSelectedFromNode] = useState<string | null>(null);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);

  const { theme } = useTheme();
  const [graphData, setGraphData] = useState<GraphData>({ nodes: [], links: [] });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const graphRef = useRef<ForceGraphMethods>();
  const containerRef = useRef<HTMLDivElement>(null);

  // Concrete colors for the canvas, resolved from the design tokens.
  // Re-read whenever the theme class flips.
  const palette = useMemo(() => {
    const styles = getComputedStyle(document.documentElement);
    const read = (name: string, fallback: string) => {
      const v = styles.getPropertyValue(name).trim();
      return v || fallback;
    };
    return {
      label: read('--text-faint', 'rgba(245, 242, 239, 0.40)'),
      labelHover: read('--text-body', '#F5F2EF'),
      link: read('--border-strong', 'rgba(255, 255, 255, 0.14)'),
      idle: read('--status-idle', '#8B8689'),
    };
  }, [theme]); // eslint-disable-line react-hooks/exhaustive-deps

  // id -> connected node ids, used for the hover-isolation emphasis
  const neighbors = useMemo(() => {
    const map = new Map<string, Set<string>>();
    graphData.links.forEach((link) => {
      const a = nodeKey(link.source);
      const b = nodeKey(link.target);
      if (!map.has(a)) map.set(a, new Set());
      if (!map.has(b)) map.set(b, new Set());
      map.get(a)!.add(b);
      map.get(b)!.add(a);
    });
    return map;
  }, [graphData]);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      // Use fetchConnectedGraphData to only get connected nodes
      const data = await fetchConnectedGraphData();
      setGraphData(data);
    } catch (err) {
      console.error('Error loading graph data:', err);
      setError('Failed to load network data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData, forceRefresh]);

  // Track the container, not the window: its height is in dvh, which moves
  // as iOS toolbars show and hide without always firing a window resize.
  // isLoading is a dep because the container only mounts once data is in.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const updateDimensions = () => {
      const rect = el.getBoundingClientRect();
      setDimensions({ width: rect.width, height: rect.height });
    };

    updateDimensions();
    const observer = new ResizeObserver(updateDimensions);
    observer.observe(el);

    return () => observer.disconnect();
  }, [isLoading, error]);

  // Set initial zoom level after graph data loads
  useEffect(() => {
    if (graphRef.current && !isLoading && graphData.nodes.length > 0) {
      // Apply a fixed zoom level from settings without animation
      graphRef.current?.zoom(forceSettings.zoomLevel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, graphData.nodes.length]);

  // Set alert message based on link adding state
  useEffect(() => {
    if (isAddingLink) {
      if (selectedFromNode) {
        setAlertMessage("Choose link target");
      } else {
        setAlertMessage("Choose link origin");
      }
    } else {
      setAlertMessage("");
    }
  }, [isAddingLink, selectedFromNode]);

  // Reset selection state when adding link mode is toggled off
  useEffect(() => {
    if (!isAddingLink) {
      setSelectedFromNode(null);
    }
  }, [isAddingLink]);

  // Apply zoom level when it changes
  useEffect(() => {
    if (graphRef.current && !isLoading) {
      graphRef.current?.zoom(forceSettings.zoomLevel);
    }
  }, [forceSettings.zoomLevel, isLoading]);

  // Update link distance when it changes
  useEffect(() => {
    if (!graphRef.current) return;

    // Update the link-force's distance accessor
    const linkForce = graphRef.current.d3Force('link');
    if (linkForce) {
      linkForce.distance(forceSettings.linkDistance);

      // Restart the simulation so the new distances take effect
      graphRef.current.d3ReheatSimulation();
    }
  }, [forceSettings.linkDistance]);

  // Update charge strength when it changes
  useEffect(() => {
    if (!graphRef.current) return;

    // Update the charge force strength
    const chargeForce = graphRef.current.d3Force('charge');
    if (chargeForce) {
      chargeForce.strength(forceSettings.chargeStrength);

      // Restart the simulation
      graphRef.current.d3ReheatSimulation();
    }
  }, [forceSettings.chargeStrength]);

  // Restart the simulation when other parameters change
  useEffect(() => {
    if (!graphRef.current || isLoading) return;

    graphRef.current.d3ReheatSimulation();
  }, [isLoading, forceSettings]);

  const handleNodeClick = useCallback((node: NodeObject) => {
    if (!node || !node.id) return;
    const nodeId = String(node.id);

    if (isAddingLink) {
      if (!selectedFromNode) {
        // First selection (origin)
        setSelectedFromNode(nodeId);
      } else if (nodeId !== selectedFromNode) {
        // Second selection (target) - add the link
        addLinkService(selectedFromNode, nodeId)
          .then(() => {
            // Reset state and refresh data
            setSelectedFromNode(null);
            setIsAddingLink(false);
            onWebsitesUpdated();
          })
          .catch(err => {
            console.error("Error adding link:", err);
            // Keep selection state for retry
          });
      }
    } else {
      // Normal navigation when not in adding link mode
      navigate(`/private-network/${nodeId}`);
    }
  }, [navigate, isAddingLink, selectedFromNode, setIsAddingLink, onWebsitesUpdated]);

  const handleNodeHover = useCallback((node: NodeObject | null) => {
    setHoveredNodeId(node && node.id ? String(node.id) : null);
  }, []);

  const isDimmed = useCallback((id: string) => {
    if (!hoveredNodeId || hoveredNodeId === id) return false;
    return !(neighbors.get(hoveredNodeId)?.has(id) ?? false);
  }, [hoveredNodeId, neighbors]);

  // Node customization
  const nodeCanvasObject = useCallback((node: NodeObject, ctx: CanvasRenderingContext2D, globalScale: number) => {
    const label = node.title || node.url;
    const nodeId = String(node.id);
    const fontSize = 10.5 / globalScale;
    const nodeR = forceSettings.nodeSize / 2; // Node radius scaled from settings

    const isSelected = isAddingLink && selectedFromNode === nodeId;
    const isGrayscale = isAddingLink && !isSelected && selectedFromNode !== nodeId;
    const isHovered = hoveredNodeId === nodeId;
    const dimmed = !isAddingLink && isDimmed(nodeId);

    const fillColor = isGrayscale ? palette.idle : (node.color || '#d95636');

    ctx.save();
    ctx.globalAlpha = dimmed ? 0.3 : 1;

    // Hover halo
    if (isHovered && !isAddingLink) {
      ctx.beginPath();
      ctx.arc(node.x ?? 0, node.y ?? 0, nodeR + 4 / globalScale + nodeR * 0.6, 0, 2 * Math.PI);
      ctx.fillStyle = fillColor;
      ctx.globalAlpha = 0.18;
      ctx.fill();
      ctx.globalAlpha = dimmed ? 0.3 : 1;
    }

    // Draw node
    ctx.beginPath();
    ctx.arc(node.x ?? 0, node.y ?? 0, nodeR, 0, 2 * Math.PI);
    ctx.fillStyle = fillColor;
    ctx.fill();

    // Draw label below the node — mono, faint at rest, bright on hover
    if (label && globalScale > 0.5) {
      const textOffsetY = nodeR * 1.5 + fontSize * 0.5;
      ctx.font = `${fontSize}px 'Geist Mono', ui-monospace, monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = isHovered ? palette.labelHover : palette.label;
      ctx.fillText(label, node.x ?? 0, (node.y ?? 0) + textOffsetY);
    }

    ctx.restore();
  }, [forceSettings.nodeSize, isAddingLink, selectedFromNode, hoveredNodeId, isDimmed, palette]);

  const linkCanvasObject = useCallback((link: LinkObject, ctx: CanvasRenderingContext2D, globalScale: number) => {
    // Get source and target node positions
    const sourceNode = link.source;
    const targetNode = link.target;

    if (typeof sourceNode !== 'object' || typeof targetNode !== 'object' || !sourceNode || !targetNode) return;

    const sourceX = sourceNode.x ?? 0;
    const sourceY = sourceNode.y ?? 0;
    const targetX = targetNode.x ?? 0;
    const targetY = targetNode.y ?? 0;

    const touchesHover =
      hoveredNodeId !== null &&
      (nodeKey(sourceNode) === hoveredNodeId || nodeKey(targetNode) === hoveredNodeId);
    const dimmed = hoveredNodeId !== null && !touchesHover;

    ctx.save();
    ctx.globalAlpha = dimmed ? 0.18 : touchesHover ? 1 : 0.8;

    // Draw link
    ctx.beginPath();
    ctx.moveTo(sourceX, sourceY);
    ctx.lineTo(targetX, targetY);
    ctx.strokeStyle = palette.link;
    ctx.lineWidth = (touchesHover ? 1.5 : 1) / globalScale;
    ctx.stroke();

    // Draw arrow at target node
    const angle = Math.atan2(targetY - sourceY, targetX - sourceX);
    const nodeR = forceSettings.nodeSize / 2; // Adjust arrow position based on node size
    const arrowLength = 8 / globalScale;
    const arrowPos = {
      x: targetX - (nodeR * Math.cos(angle)),
      y: targetY - (nodeR * Math.sin(angle))
    };

    ctx.beginPath();
    ctx.moveTo(arrowPos.x, arrowPos.y);
    ctx.lineTo(
      arrowPos.x - arrowLength * Math.cos(angle - Math.PI / 7),
      arrowPos.y - arrowLength * Math.sin(angle - Math.PI / 7)
    );
    ctx.lineTo(
      arrowPos.x - arrowLength * Math.cos(angle + Math.PI / 7),
      arrowPos.y - arrowLength * Math.sin(angle + Math.PI / 7)
    );
    ctx.lineTo(arrowPos.x, arrowPos.y);
    ctx.fillStyle = palette.link;
    ctx.fill();

    ctx.restore();
  }, [palette, forceSettings.nodeSize, hoveredNodeId]);

  // Legend chips: distinct node colors present in the network
  const legend = useMemo(() => {
    const seen = new Map<string, string>();
    graphData.nodes.forEach((node) => {
      const color = (node.color || '#d95636').toLowerCase();
      if (!seen.has(color)) {
        seen.set(color, COLOR_LABELS[color] || color);
      }
    });
    return Array.from(seen, ([color, label]) => ({ color, label }));
  }, [graphData.nodes]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div
            className="w-10 h-10 rounded-full animate-spin mx-auto"
            style={{
              border: '3px solid var(--border-hairline)',
              borderTopColor: 'var(--accent)',
              marginBottom: 'var(--space-4)',
            }}
          />
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>Loading network...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div
          className="text-center max-w-md"
          style={{
            background: 'var(--surface-raised)',
            border: '1px solid var(--border-hairline)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-6)',
          }}
        >
          <div style={{ color: 'var(--status-down)', marginBottom: 'var(--space-3)' }}>
            <Icon name="network" size={28} />
          </div>
          <h2
            style={{
              fontSize: 'var(--text-md)',
              fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
              color: 'var(--text-body)',
              marginBottom: 'var(--space-2)',
            }}
          >
            Network Error
          </h2>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginBottom: 'var(--space-4)' }}>
            {error}
          </p>
          <Button size="sm" icon="refresh-cw" onClick={loadData}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full">
      <div ref={containerRef} className="h-full overflow-hidden relative">
        {graphData.nodes.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full">
            <div
              className="text-center max-w-md"
              style={{
                background: 'var(--surface-raised)',
                border: '1px solid var(--border-hairline)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-8)',
              }}
            >
              <div style={{ color: 'var(--text-faint)', marginBottom: 'var(--space-3)' }}>
                <Icon name="globe" size={28} />
              </div>
              <h2
                style={{
                  fontSize: 'var(--text-md)',
                  fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                  color: 'var(--text-body)',
                  marginBottom: 'var(--space-2)',
                }}
              >
                No Websites in Network
              </h2>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
                Add websites to visualize their network connections.
              </p>
            </div>
          </div>
        ) : (
          <>
            {isAddingLink && alertMessage && (
              <div
                className="absolute top-0 left-0 right-0 z-10 m-4 flex items-center ck-menu-pop"
                style={{
                  background: 'var(--surface-raised)',
                  border: '1px solid var(--border-strong)',
                  borderRadius: 'var(--radius-md)',
                  boxShadow: 'var(--shadow-pop)',
                  padding: '12px 14px',
                  gap: '10px',
                }}
              >
                <Icon name="circle-alert" size={16} style={{ color: 'var(--accent-text)', flexShrink: 0 }} />
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>{alertMessage}</span>
                <span className="ml-auto">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setIsAddingLink(false);
                      setSelectedFromNode(null);
                    }}
                  >
                    Cancel
                  </Button>
                </span>
              </div>
            )}
            <ForceGraph2D
              ref={graphRef}
              graphData={graphData}
              width={dimensions.width || containerRef.current?.clientWidth || window.innerWidth}
              height={dimensions.height || containerRef.current?.clientHeight || window.innerHeight - 200}
              backgroundColor="rgba(0,0,0,0)"
              nodeLabel={(node) => node.title || node.url}
              nodeCanvasObject={nodeCanvasObject}
              linkCanvasObject={linkCanvasObject}
              linkDirectionalArrowLength={3}
              linkDirectionalArrowRelPos={1}
              linkDirectionalParticles={forceSettings.particleCount}
              linkDirectionalParticleSpeed={forceSettings.particleSpeed}
              onNodeClick={handleNodeClick}
              onNodeHover={handleNodeHover}
              d3AlphaMin={0.0001}
              cooldownTime={7000}
              d3AlphaDecay={0.01}
              d3VelocityDecay={0.2}
              nodeRelSize={forceSettings.nodeSize}
              minZoom={0.1}
              maxZoom={8}
              enableNodeDrag={true}
              enableZoomInteraction={true}
              enablePanInteraction={true}
            />

            {/* Legend — distinct node colors in the network */}
            {legend.length > 0 && (
              <div
                className="absolute z-10"
                style={{ top: '14px', left: '16px', display: 'flex', flexWrap: 'wrap', gap: '14px', pointerEvents: 'none' }}
              >
                {legend.map((entry) => (
                  <span
                    key={entry.color}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '7px',
                      fontSize: 'var(--text-xs)',
                      color: 'var(--text-muted)',
                      fontFamily: COLOR_LABELS[entry.color] ? 'var(--font-ui)' : 'var(--font-mono)',
                    }}
                  >
                    <span
                      style={{
                        width: '9px',
                        height: '9px',
                        borderRadius: 'var(--radius-pill)',
                        background: entry.color,
                      }}
                    />
                    {entry.label}
                  </span>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
